/**
 * Global 401 guard for the ENTIRE page, not just the shell's own Angular app.
 *
 * WHY THIS FILE EXISTS
 * ---------------------------------------------------------------------------
 * This app is composed of multiple independently-bootstrapped Angular
 * applications loaded via Webpack Module Federation (the shell itself, plus
 * remotes such as nxi-ng-balances, nxi-ng-account, etc. - see
 * loadRemoteModule() usages in page-item.component.ts / edc-wrapper.component.ts).
 *
 * Each remote calls its own `platform.bootstrapModule(...)`, which creates its
 * OWN root Angular Injector. Angular's `HTTP_INTERCEPTORS` mechanism (see
 * app/interceptors/backend.interceptor.ts) is scoped to whichever Injector
 * registered it - it cannot see HttpClient calls made from a different
 * Angular app's Injector, even though that other app is a "remote" loaded
 * into the very same browser tab. In other words: BackendInterceptor only
 * ever sees requests made by services that live inside the shell's own
 * Angular module tree (e.g. auth.service.ts). It never sees requests from
 * BalancesService (nxi-ng-balances) or similar services in other MFEs.
 *
 * HOW THIS FILE GETS AROUND THAT
 * ---------------------------------------------------------------------------
 * Every MFE, regardless of which Angular Injector it belongs to, is still
 * just JavaScript running in the SAME browser window/global scope (Module
 * Federation loads remote bundles into the current page - it does not use
 * iframes). That means there is only ONE `window.XMLHttpRequest` constructor
 * and ONE `window.fetch` function shared by every MFE on the page.
 * Angular's HttpClient (via HttpXhrBackend) calls `new XMLHttpRequest()`
 * under the hood at request time - it does not cache a private reference to
 * the constructor ahead of time. So if we monkey-patch
 * `XMLHttpRequest.prototype.open/send` and `window.fetch` BEFORE any MFE
 * (including the shell itself) issues its first HTTP call, every single
 * XHR/fetch call from every MFE flows through this wrapper - no matter which
 * Angular Injector, module, or micro-frontend made the call, and without
 * having to add an interceptor/provider to each MFE individually.
 *
 * WHY installGlobalXhrGuard() IS A FUNCTION AND NOT A TOP-LEVEL SIDE EFFECT
 * ---------------------------------------------------------------------------
 * Patching happens inside an explicit, idempotent install function rather
 * than running automatically the moment this file is imported. This keeps
 * the module free of import-time side effects (so importing it in a test
 * doesn't silently mutate global XHR/fetch) and lets unit tests install the
 * guard on demand, after spying on the pre-patch XHR/fetch implementations.
 * The idempotency guard (`installed` flag) also protects against
 * double-patching if this ever gets called more than once (e.g. re-run in a
 * test suite, or accidentally invoked twice during bootstrap).
 *
 * TIMING IS CRITICAL IN PRODUCTION: installGlobalXhrGuard() must be called
 * as the very first thing in bootstrap.ts, before `platform.bootstrapModule
 * (AppModule)` runs and before any remote is dynamically loaded via
 * loadRemoteModule(). Remotes are only ever loaded lazily at runtime (on
 * user navigation), well after this has already patched the global objects,
 * so this ordering is safe in practice.
 *
 * WHAT IT DOES
 * ---------------------------------------------------------------------------
 * On any XHR or fetch response with HTTP status 401 - but ONLY when
 * `window.appSettings.isLoggedIn` is true (see handleUnauthorized() below) -
 * it:
 *   1. Fires a POST to the session logout endpoint so the server actually
 *      invalidates the (now-stale) session, using `credentials: 'include'`
 *      so the session cookie is sent along.
 *   2. Redirects the browser to the domain root ('/') once that logout call
 *      settles (success or failure - the goal is just to get the user off
 *      the dead session regardless of the logout call's own outcome).
 *
 * The isLoggedIn check exists because pre-login pages (landing, sign-on,
 * MFA, etc.) also make XHR/fetch calls that legitimately 401 as part of the
 * normal login flow - without gating on login state, this guard was firing
 * the logout+redirect dance on those pages too.
 *
 * A guard against the logout URL itself is included so that a 401 response
 * FROM the logout call does not recursively trigger another logout call.
 */

export const LOGOUT_URL = '/investordasfrest/api/session/logout';

let installed = false;

/**
 * Test-only hook: clears the idempotency flag so a spec can call
 * installGlobalXhrGuard() again and have it re-capture window.fetch/
 * XMLHttpRequest.prototype (which the spec has re-spied on in its own
 * beforeEach) instead of silently no-op'ing. Never call this from
 * application code - production only ever installs the guard once, from
 * bootstrap.ts, and re-installing at runtime would leave the previous
 * patch layer's closures (and their `originalFetch`/`originalXhrSend`
 * captures) orphaned but still technically live.
 */
export function resetGlobalXhrGuardForTesting(): void {
  installed = false;
}

/**
 * Patches the global XMLHttpRequest/fetch APIs so that any 401 response,
 * from any MFE on the page, triggers session logout + redirect to '/'.
 * Safe to call more than once - only the first call actually patches.
 */
export function installGlobalXhrGuard(): void {
  if (installed) {
    return;
  }
  installed = true;

  // Captured so we always call the *original* browser implementation, even
  // after we've overwritten `XMLHttpRequest.prototype.open/send` and
  // `window.fetch` below - otherwise we'd recurse into our own patched
  // version forever.
  const originalXhrOpen = XMLHttpRequest.prototype.open;
  const originalXhrSend = XMLHttpRequest.prototype.send;
  const originalFetch = window.fetch.bind(window);

  /**
   * Fires the session logout call and redirects to '/' once it settles.
   * Shared by both the XHR and fetch patches below so the behavior (and any
   * future change to it) stays in exactly one place.
   */
  function handleUnauthorized(requestUrl: string | undefined): void {
    // Only act for an already-authenticated session. Pre-login pages
    // (landing, sign-on, MFA, etc.) legitimately issue XHR/fetch calls that
    // come back 401 as part of the normal login flow itself - without this
    // check, this guard was firing the logout+redirect dance on those pages
    // too, which is the "unexpected behavior on prelogin pages" this check
    // was added to fix. `window.appSettings` is the same global the app
    // itself keeps in sync with login state elsewhere (see
    // app.component.ts's preLoginPagesLogic()/postLoginPagesLogic(), which
    // toggle `(window as any).appSettings.isLoggedIn`) - read directly off
    // window here since this file runs outside Angular's DI entirely.
    // Re-read on every call (not cached) since login state changes during
    // the page's lifetime (login/logout without a full page reload).
    const appSettings = (window as any).appSettings;
    if (!appSettings || !appSettings.isLoggedIn) {
      return;
    }

    // Avoid infinite recursion: a 401 from the logout call itself must not
    // trigger another logout call.
    if (requestUrl && requestUrl.includes(LOGOUT_URL)) {
      return;
    }

    originalFetch(LOGOUT_URL, { method: 'POST', credentials: 'include' })
      // Redirect regardless of whether the logout call itself succeeded -
      // the priority is getting the user off the stale/rejected session.
      .finally(() => {
        window.location.href = '/';
      });
  }

  // -----------------------------------------------------------------------
  // Patch #1: XMLHttpRequest - covers every MFE/library that issues requests
  // the "classic" way, including Angular's default HttpXhrBackend.
  // -----------------------------------------------------------------------
  // Assigning through `(prototype as any).method = ...` (rather than
  // `prototype.method = ...` directly) deliberately sidesteps TypeScript's
  // overload-compatibility checking for XMLHttpRequest.open/fetch's typed
  // signatures. This is the conventional pattern for monkey-patching built-in
  // browser APIs: the patched function's shape (extra/rest args, `this`
  // context) doesn't need to satisfy every overload the lib.dom.d.ts
  // declares - only the actual browser runtime behavior matters here.
  (XMLHttpRequest.prototype as any).open = function patchedOpen(
    this: XMLHttpRequest,
    method: string,
    url: string | URL,
    ...rest: any[]
  ) {
    // Stash the request URL on the instance so `send`'s `loadend` handler
    // below can identify 401s from the logout call itself (see guard above).
    (this as any).__guardedUrl = url?.toString();
    return originalXhrOpen.call(this, method, url as string, ...(rest as []));
  };

  (XMLHttpRequest.prototype as any).send = function patchedSend(this: XMLHttpRequest, ...args: any[]) {
    // 'loadend' fires for every terminal state (success, error, abort, timeout),
    // so this.status is reliably populated by the time it runs - unlike
    // 'load', which would miss network-level failures.
    this.addEventListener('loadend', () => {
      if (this.status === 401) {
        handleUnauthorized((this as any).__guardedUrl);
      }
    });
    return originalXhrSend.apply(this, args as []);
  };

  // -----------------------------------------------------------------------
  // Patch #2: fetch - covers any MFE/library (including this file's own call
  // to `originalFetch` above, which intentionally bypasses this wrapper)
  // that uses the fetch API directly instead of XMLHttpRequest.
  // -----------------------------------------------------------------------
  (window as any).fetch = async function patchedFetch(
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> {
    const response = await originalFetch(input, init);
    if (response.status === 401) {
      handleUnauthorized(typeof input === 'string' ? input : input.toString());
    }
    return response;
  };
}
